import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useRouter } from '@/lib/navigation';
import { format } from 'date-fns';
import type { TripFormValues } from '@/components/trips/form/TripForm';
import { queryKeys } from '@/lib/query/query-keys';
import { apiFetch } from '@/lib/api/client';
import { mapTripListItem, type RawTripListItem } from '@/lib/api/adapters/trips';

interface ApiErrorPayload {
  error?: {
    message?: string;
  };
}

function extractErrorMessage(payload: unknown, fallback: string): string {
  if (payload && typeof payload === 'object' && 'error' in payload) {
    const errorContainer = payload as ApiErrorPayload;
    const message = errorContainer.error?.message;
    if (typeof message === 'string' && message.trim().length > 0) {
      return message;
    }
  }

  return fallback;
}

export interface TripPayloadOverrides {
  permanentResidenceCountryId?: number;
  permanentResidenceCityId?: number;
}

export function buildTripRequestPayload(
  data: TripFormValues,
  overrides: TripPayloadOverrides = {}
) {
  if (!data.startDate || !data.endDate) {
    throw new Error('Start and end dates are required');
  }

  const initiatorId = Number(data.initiator);
  if (!Number.isFinite(initiatorId)) {
    throw new Error('Initiator selection is required.');
  }
  const substituteEmployeeId = data.actingEmployee ? Number(data.actingEmployee) : 0;

  const rawCountryId =
    overrides.permanentResidenceCountryId ?? Number.parseInt(data.permanentResidenceCountry, 10);
  if (typeof rawCountryId !== 'number' || Number.isNaN(rawCountryId)) {
    throw new Error('Permanent residence country selection is required.');
  }
  const permanentResidenceCountryId = rawCountryId;

  const rawCityId =
    overrides.permanentResidenceCityId ?? Number.parseInt(data.permanentResidenceCity, 10);
  if (typeof rawCityId !== 'number' || Number.isNaN(rawCityId)) {
    throw new Error('Permanent residence city selection is required.');
  }
  const permanentResidenceCityId = rawCityId;

  const payload: Record<string, unknown> = {
    name: data.tripName,
    initiator_id: initiatorId,
    substitute_employee_id:
      typeof substituteEmployeeId === 'number' && Number.isFinite(substituteEmployeeId)
        ? substituteEmployeeId
        : 0,
    trip_type: data.tripType,
    roaming_request: data.roamingExpense,
    start_date: format(data.startDate, 'yyyy-MM-dd'),
    end_date: format(data.endDate, 'yyyy-MM-dd'),
    purpose: data.purpose,
    comment: data.additionalComment ?? null,
    permanent_residence_country_id: permanentResidenceCountryId,
    permanent_residence_city_id: permanentResidenceCityId,
    need_daily_payout: data.accommodationExpense,
  };

  return payload;
}

async function createTrip(data: TripFormValues) {
  const payload = buildTripRequestPayload(data);

  const response = await apiFetch('/api/v1/business-trip-requests', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(payload),
  });

  const body = await response.json().catch(() => null);

  if (!response.ok) {
    const message = extractErrorMessage(
      body,
      `Failed to create trip: ${response.status} ${response.statusText}`
    );
    throw new Error(message);
  }

  if (!body || typeof body !== 'object') {
    throw new Error('Trip creation succeeded but no trip data was returned.');
  }

  return mapTripListItem(body as RawTripListItem);
}

export function useCreateTrip() {
  const queryClient = useQueryClient();
  const router = useRouter();

  return useMutation({
    mutationFn: createTrip,
    onSuccess: (trip) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.trips });
      router.push(`/trips/${trip.id}`);
    },
    onError: (error: Error) => {
      console.error('Failed to create trip:', error);
    },
  });
}
